import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { Tabs } from '@com-delta-omnichannel/mach-core-js';
import { FlyoutAccountSummaryHeader } from '../../models/interfaces/account-summary-header.models';
import { FlyoutFooter } from '../../models/interfaces/footer.models';
import { FlyoutStatusTracker } from '../../models/interfaces/status-tracker.interfaces';
import { FlyoutToday } from '../../models/interfaces/today.models';
import { FlyoutAccountSummaryHeaderComponent } from '../account-summary-header/flyout-account-summary-header.component';
import { FlyoutFooterComponent } from '../footer/flyout-footer.component';
import { FlyoutStatusTrackerComponent } from '../status-tracker/flyout-status-tracker.component';
import { FlyoutTodayComponent } from '../today/flyout-today.component';
import { FlyoutTrackerBaseComponent } from './base/flyout-tracker-base.component';

@Component({
  selector: 'idp-flyout',
  standalone: true,
  imports: [
    CommonModule,
    FlyoutAccountSummaryHeaderComponent,
    FlyoutTodayComponent,
    FlyoutStatusTrackerComponent,
    FlyoutFooterComponent
  ],
  templateUrl: './flyout-tracker.component.html',
  styleUrls: ['./flyout-tracker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FlyoutTrackerComponent
  extends FlyoutTrackerBaseComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() accountSummaryHeaderContent: FlyoutAccountSummaryHeader =
    {} as FlyoutAccountSummaryHeader;

  @Input() todayTabContent: FlyoutToday = {} as FlyoutToday;
  @Input() statusTracker: FlyoutStatusTracker = {} as FlyoutStatusTracker;
  @Input() footerContent = {} as FlyoutFooter;
  @Input() isAlertAdvisory = false;
  @Output() logoutEvent = new EventEmitter();
  @Output() closeEvent = new EventEmitter();
  @Output() tabClickTracking = new EventEmitter<HTMLElement>();
  @Output() errorAnalyticsData = new EventEmitter<string>();

  @HostListener('document:keydown', ['$event'])
  handleTabKeyWithInModal(event: KeyboardEvent): void {
    this.handleTabKey(
      event,
      '#flyout-tracker--container',
      'input,button,select,textarea,a,[tabindex]:not([tabindex="-1"])'
    );
  }

  ngOnInit(): void {
    const container = document.getElementById('flyout-tracker--container');
    if (container) {
      container.focus();
    }
  }

  ngAfterViewInit(): void {
    this.initializeTabs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['todayTabContent'] && !changes['todayTabContent'].firstChange) {
      setTimeout(() => this.initializeTabs(), 100);
    }
  }

  private initializeTabs(): void {
    setTimeout(() => {
      const shouldShowTabs =
        this.todayTabContent.todayTrip &&
        this.todayTabContent.todayTrip.scheduleFlightDepartureTime;

      if (shouldShowTabs) {
        this.createTabItems();
        this.createTabsInstance();
      }
    }, 100);
  }

  private createTabItems(): void {
    this.tabsOptions.tabItems = [
      {
        id: 'today-tab',
        label: this.accountSummaryHeaderContent.labels?.todayLabel || 'Today',
        value: 'today-tab',
        selected: this.selectedTab === 0
      },
      {
        id: 'flight-tab',
        label:
          this.accountSummaryHeaderContent.labels?.myFlightLabel || 'My Flight',
        value: 'flight-tab',
        selected: this.selectedTab === 1
      }
    ];
  }

  private createTabsInstance(): void {
    const tabElement = document.getElementById('flyout-tabs');
    if (tabElement) {
      this.tabsInstance = new Tabs(this.tabsOptions);
      this.onTabClick();
    }
  }

  onTabClick(): void {
    this.tabsInstance.onTabChange((index) => {
      this.selectedTab = index;
      const tabValue = this.trackTabAnalytics(index);
      if (tabValue) {
        this.tabClickTracking.emit(tabValue);
      }
    });
  }

  switchTab(n: number): void {
    this.selectedTab = n;
  }

  handleTabKey(e: KeyboardEvent, modelId: string, tagsList: string): void {
    if (e.key === 'Tab') {
      const modalElement = document.querySelector(modelId) as HTMLElement;
      const modalTagList = modalElement.querySelectorAll(tagsList);

      if (modalTagList.length) {
        this.focusTab(Array.from(modalTagList) as HTMLElement[], e);
      }
    }
  }

  close(): void {
    this.closeEvent.emit();
  }
}
